import React, { useEffect, useState, useMemo, useCallback } from "react";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { ColumnConfig } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { ReportStatusConverter } from "@app/reports/reportsModel";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { getMostRecentSaturday, getNextMostRecentSaturday } from "@app/shared/utility/getMostRecentDay";
import OperationsService from "@app/operations/operationsService";
import { ExportTypeEnum } from "@app/operations/exportTypeEnum";
import { HumanaReportInfoResult } from "@app/operations/jobStatisticsResult";
interface HumanaExportStatusesProps {
  clients: { clientId: number; name: string }[];
  users: { userId: number; userName: string }[];
  setUsers: React.Dispatch<React.SetStateAction<{ userId: number; userName: string }[]>>;
  clientId: number | null;
  setClientId: React.Dispatch<React.SetStateAction<number | null>>;
}
const operationsService = new OperationsService();
const HumanaExportStatusColumns: ColumnConfig = {

  /* oxlint-disable sort-keys */
  projectName: {
    displayText: "Project Name",
    sizeContentToFit: true,
    testId: "humana-export-statuses__project-name",
  },
  projectId: {
    displayText: "Project Id",
    sizeContentToFit: true,
    testId: "humana-export-statuses__project-id",
  },
  adequacyServicingProviderStatus: {
    displayText: "Adequacy Servicing Provider Status",
    sizeContentToFit: true,
    testId: "humana-export-statuses__adequacyServicingProviderStatus",
  },
  adequacySummaryStatus: {
    displayText: "Adequacy Summary Status",
    sizeContentToFit: true,
    testId: "humana-export-statuses__adequacySummaryStatus",
  },
  adequacySummaryBulkStatus: {
    displayText: "Adequacy Summary Bulk Status",
    sizeContentToFit: true,
    testId: "humana-export-statuses__adequacySummaryBulkStatus",
  }
  /* oxlint-enable sort-keys */
};

const HumanaExportStatuses: React.FC<HumanaExportStatusesProps> = ({ clients, users, setUsers, clientId, setClientId }) => {
  const [humanaExportStats, setHumanaExportStats] = useState<HumanaReportInfoResult[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasFetchedData, setHasFetchedData] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [runDate, setRunDate] = useState<string>(getMostRecentSaturday());
  const [limitRunDate, setLimitRunDate] = useState<string>(getNextMostRecentSaturday());
  const [userId, setUserId] = useState<number | null>(null);
  const [selectedAdequacyServicingFilter, setSelectedAdequacyServicingFilter] = useState<string>("");
  const [selectedAdequacySummaryFilter, setSelectedAdequacySummaryFilter] = useState<string>("");
  const [selectedAdequacySummaryBulkFilter, setSelectedAdequacySummaryBulkFilter] = useState<string>("");

  const EXPORT_TYPES = [
    ExportTypeEnum.AdequacyServicingProvider,
    ExportTypeEnum.AdequacySummary,
    ExportTypeEnum.AdequacySummaryBulk
  ];
  const PROJECT_SELECT_ERROR_TEXT = "Disabled";

  useEffect(() => {
    resetFormValues();
  }, [clientId]);

  const resetFormValues = () => {
    setError(null);
    setHumanaExportStats(null);
  };

  useEffect(() => {
    if (!!clientId) {
      fetchUsers(clientId)
    }
  }, []);

  useEffect(() => { 
    if (!!userId && !hasFetchedData) { 
      getProjectExportStatuses();
      setHasFetchedData(true);
    }
  }, [userId, hasFetchedData]);

  useEffect(() => {
    if (clients.find((c) => c.clientId === clientId)?.name === "Humana") {
      const integrationUser = users.find((d) => d.userName === "integration.user.humana@questanalytics.com");
      setUserId(integrationUser?.userId || null);
    } else {
      setUserId(null);
    }
  }, [clients, clientId, users]);

  const fetchUsers = async (clientId: number) => {
    try {
      const userData = await operationsService.getMinimalUsersByClient(clientId);
      userData.sort((a, b) => a.userName > b.userName ? 1 : -1);
      setUsers(userData);
    } catch (error) {
    }
  };

  const getProjectExportStatuses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!!clientId && !!runDate && !!userId) {
        const response = await operationsService.getExportStatusesByClient(
          clientId,
          userId,
          runDate,
          limitRunDate,
          EXPORT_TYPES
        );

        const adequacyServicingFilter = selectedAdequacyServicingFilter ? response.filter(item => item.adequacyServicingProviderStatus.toUpperCase() === selectedAdequacyServicingFilter) : response;
        const adequacySummaryFilter = selectedAdequacySummaryFilter ? adequacyServicingFilter.filter(item => item.adequacySummaryStatus.toUpperCase() === selectedAdequacySummaryFilter) : adequacyServicingFilter;
        const adequacyBulkFilter = selectedAdequacySummaryBulkFilter ? adequacySummaryFilter.filter(item => item.adequacySummaryBulkStatus.toUpperCase() === selectedAdequacySummaryBulkFilter) : adequacySummaryFilter;
          
        setHumanaExportStats(adequacyBulkFilter);
      }
    } catch (error) {
      setError("Error: Invalid Date, Client, or User");
    } finally {
      setLoading(false);
    }
  }, [clientId,
    userId,
    limitRunDate,
    runDate,
    selectedAdequacyServicingFilter,
    selectedAdequacySummaryFilter,
    selectedAdequacySummaryBulkFilter]);

  const tableData = useMemo(() => {
    if (humanaExportStats) {
      return humanaExportStats.map((stat, index) => ({
        /* oxlint-disable sort-keys */
        key: index.toString(),
        projectId: stat.projectId,
        projectName: stat.projectName,
        adequacyServicingProviderStatus: stat.adequacyServicingProviderStatus.toString() !== "initialized" ? stat.adequacyServicingProviderStatus.toUpperCase() : "NOT RUN",
        adequacySummaryStatus: stat.adequacySummaryStatus.toString() !== "initialized" ? stat.adequacySummaryStatus.toUpperCase() : "NOT RUN",
        adequacySummaryBulkStatus: stat.adequacySummaryBulkStatus.toString() !== "initialized" ? stat.adequacySummaryBulkStatus.toUpperCase() : "NOT RUN"
        /* oxlint-enable sort-keys */
      }));
    }
  }, [humanaExportStats]);

  const isClientSelectionInvalid = clientId === null;

  let calculateDisabledTooltipText = "";
  if (!runDate || !limitRunDate || !clientId || !userId) {
    calculateDisabledTooltipText = PROJECT_SELECT_ERROR_TEXT;
  }

  return (
    <div>
      <div className="form-row mb-3">
        <div className="col pe-1">
          <QESSelect
            label="Client"
            id="clientSelect"
            value={clientId ?? ""}
            onChange={(event) => {
              const selectedClientId = Number(event.target.value);
              setClientId(selectedClientId);
              fetchUsers(selectedClientId);
            }}
            placeholder={clients.length === 0 ? "Loading Clients..." : "Select Client"}
            testId="humana-export-statuses__client-select-humana"
            required
            disabled={clients.length === 0}
            isValid={!isClientSelectionInvalid}
            errorText={"Invalid Client"}
            positionDescriptionTextAbsolutely={true}
          >
            {clients && clients.map((client) => (
              <option key={client.clientId} value={client.clientId.toString()}>
                {client.name}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col px-1">
          <QESSelect
            id="userSelect"
            value={userId !== null ? userId.toString() : ''}
            onChange={(event) => setUserId(Number(event.target.value))}
            placeholder="Select User"
            testId="weekend-run-monitoring__user-select-humana"
            label="User"
            required
            isValid
            disabled={clientId === null}
          >
            <option value="">Select User</option>
            {users.map((user) => (
              <option key={user.userId} value={user.userId.toString()}>
                {user.userName}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col px-1">
          <QESDateInput
            label="Run Date"
            value={runDate}
            onChange={(event) => setRunDate(event.target.value)}
            testId="humana-export-statuses__run-date-input-humana"
          />
        </div>
        <div className="col px-1">
          <QESDateInput
            label="Upper Limit Run Date"
            value={limitRunDate}
            onChange={(event) => setLimitRunDate(event.target.value)}
            testId="humana-export-statuses__limit-run-date-input-humana"
          />
        </div>
        <div className="col d-flex align-items-end ps-1">
          <TooltipWrapper position="top" text={calculateDisabledTooltipText}>
            <QESButton
              disabled={!runDate || !limitRunDate || !clientId || !userId}
              onClick={getProjectExportStatuses}
              qesType="primary"
              qesTestId="humana-export-statuses__calculate-weekly-job-statistics-humana"
            >
              Search
            </QESButton>
          </TooltipWrapper>
        </div>
      </div>
      <div className="form-row mb-3">
      <div className="col pe-1">
        <QESSelect
          allowEmpty={true}
          containerClassName="jobs-list__requeue-job-queue-list"
          testId="jobs-list__requeue-list"
          label="Adequacy Servicing Provider Status Filter"
            placeholder="Select a Status Filter"
            onChange={(e) => setSelectedAdequacyServicingFilter((e.target.value === "NOT RUN" ? "INITIALIZED" : e.target.value))}>
          {Object.values(ReportStatusConverter).map(item => (
            <option key={item}>
              {item.toUpperCase() !== "INITIALIZED" ? item.toUpperCase() : "NOT RUN"}
            </option>
          ))}
          </QESSelect>
        </div>
        <div className="col px-1">
          <QESSelect
            allowEmpty={true}
            containerClassName="jobs-list__requeue-job-queue-list"
            testId="jobs-list__requeue-list"
            label="Adequacy Summary Status Filter"
            placeholder="Select a Status Filter"
            onChange={(e) => setSelectedAdequacySummaryFilter((e.target.value === "NOT RUN" ? "INITIALIZED" : e.target.value))}>
            {Object.values(ReportStatusConverter).map(item => (
              <option key={item}>
                {item.toUpperCase() !== "INITIALIZED" ? item.toUpperCase() : "NOT RUN"}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col ps-1">
          <QESSelect
            allowEmpty={true}
            containerClassName="jobs-list__requeue-job-queue-list"
            testId="jobs-list__requeue-list"
            label="Adequacy Summary Bulk Status Filter"
            placeholder="Select a Status Filter"
            onChange={(e) => setSelectedAdequacySummaryBulkFilter((e.target.value === "NOT RUN" ? "INITIALIZED" : e.target.value))}>
            {Object.values(ReportStatusConverter).map(item => (
              <option key={item}>
                {item.toUpperCase() !== "INITIALIZED" ? item.toUpperCase() : "NOT RUN"}
              </option>
            ))}
          </QESSelect>
        </div>
      </div>
      <div className="mt-4">
        {loading && <LoadingSpinner />}
        {error && <div>{error}</div>}
        {humanaExportStats && (
          <Waffles
            columns={HumanaExportStatusColumns}
            data={tableData}
            enablePagination={false}
            passThroughSorting={true}
            testId="humana-export-statuses__project-statistics-table-humana"
          />
        )}
      </div>
    </div>
  );
};
export default HumanaExportStatuses;